import { CapacityModel } from "store/capacity/capacityStateModel";
import { 
    faBarcode, faBolt, faBatteryBolt, faMapMarkerCheck, faFileContract, faShuttleVan, faDatabase, faRulerVertical
} from '@fortawesome/pro-duotone-svg-icons';


export const pdfSuffix = {
    "pre-covid": "PRE_COVID",
    "post-covid": "POST_COVID"
}

export const numberFormatter = (num) => {
    const convertedNumber = Number(num);
    const formatted = convertedNumber.toLocaleString("en-US");
    
    return isNaN(convertedNumber) || num === null ? num : formatted;
};

export const thousands = value => `${value}`.replace(/(?=(?!(\b))(\d{3})+$)/g, '$1,');

export const consumptionTableHeaders = [
    { field: 'totalConsumption', headerName: 'Total Consumption (kWh)' },
    { field: 'redConsumption', headerName: 'Red Consumption (kWh)' },
    { field: 'amberConsumption', headerName: 'Amber Consumption (kWh)'},
    { field: 'greenConsumption', headerName: 'Green Consumption (kWh)' },
    { field: 'reactiveConsumption', headerName: 'Reactive (kVArh)' },
    { field: 'maxDemand', headerName: 'Max Demand (kVA)' },
    { field: 'maxDemandTime', headerName: 'Max Demand Time' }
];

export const costTableHeaders = [
    { field: 'prevTotalCost', headerName: 'Prev. Total Cost (£)' },
    { field: 'newTotalCost', headerName: 'New Total Cost (£)' },
    { field: 'totalCostDiff', headerName: 'Total Change in Cost (£)' },
    { field: 'totalCostPercentChange', headerName: 'Total Percentage Change (%)' },

    { field: 'prevFixedCost', headerName: 'Prev. Capacity Cost (£)' },
    { field: 'newFixedCost', headerName: 'New Capacity Cost (£)' },
    { field: 'fixedCostDiff', headerName: 'Change in Cost (£)' },
    { field: 'fixedCostPercentChange', headerName: 'Percentage Change (%)' },

    { field: 'prevTimeOfUseCost', headerName: 'Prev. Time of Use Cost (£)' },
    { field: 'newTimeOfUseCost', headerName: 'New Time of Use Cost (£)' },
    { field: 'timeOfUseCostDiff', headerName: 'Change in Cost (£)' },
    { field: 'timeOfUseCostPercentChange', headerName: 'Percentage Change (%)' },
    
    { field: 'prevReactiveCost', headerName: 'Prev. Reactive Cost (£)' },
    { field: 'newReactiveCost', headerName: 'New Reactive Cost (£)' },
    { field: 'reactiveCostDiff', headerName: 'Change in Cost (£)' },
    { field: 'reactiveCostPercentChange', headerName: 'Percentage Change (%)' },
];

export const costGroups = ["Total Cost", "Capacity & Fixed Cost", "Time Of Use Cost (RAG)", "Reactive Power Cost"];

export const rateTableHeaders = [
    { field: 'year', headerName: 'BSC Year' },
    { field: 'red', headerName: 'Red Rate (p/kWh)' },
    { field: 'amber', headerName: 'Amber Rate (p/kWh)' },
    { field: 'green', headerName: 'Green Rate (p/kWh)' },
    { field: 'standing', headerName: 'Standing Rate (p/MPAN/day)' },
    { field: 'capacity', headerName: 'Capacity Rate (p/kVA/day)' },
    { field: 'exceededCapacity', headerName: 'Excess Capacity Rate (p/kVA/day)' },
    { field: 'reactivePower', headerName: 'Reactive Power Rate (p/kVArh)' },
];

export const duosTableHeaders = [
    { field: 'row', headerName: '' },
    { field: 'red', headerName: 'Red Unit' },
    { field: 'amber', headerName: 'Amber Unit' },
    { field: 'green', headerName: 'Green Unit' },
    { field: 'standing', headerName: 'Standing Charge' },
    { field: 'capacity', headerName: 'Capacity Charge' },
    { field: 'exceededCapacity', headerName: 'Excess Capacity Charge' },
    { field: 'reactivePower', headerName: 'Reactive Power Charge' },
];

export const bandHeaders = [
    {
        field: 'band',
        headerName: 'Bands'
    },
    {
        field: 'lower',
        headerName: 'Lower (kVA)'
    },
    {
        field: 'upper',
        headerName: 'Upper (kVA)'
    }
];

export const metaMpan = (mpan: string, topLine: string) => {
    let profileClass = topLine.slice(0, 2);
    let meterTimeswitchCode = topLine.slice(2, 5);
    let lineLossFactorClass = topLine.slice(5, 8);
    let distributorId = mpan?.slice(0, 2);
    let uniqueIdentifier = mpan?.slice(2, 10);
    let checkDigit = mpan?.slice(10, 13);

    return [
        profileClass,
        meterTimeswitchCode,
        lineLossFactorClass,
        distributorId,
        uniqueIdentifier,
        checkDigit
    ]
}

export const snippets = (capacity: CapacityModel) => [
    {
        name: "Old Topline",
        icon: faDatabase,
        text: capacity?.oldTopline
    },
    {
        name: "Max. Import Capacity",
        icon: faBolt,
        text: `${capacity?.importCapacity} kVA`
    },
    {
        name: "Voltage",
        icon: faBatteryBolt,
        text: capacity?.voltage
    },
    {
        name: "GSP Group",
        icon: faMapMarkerCheck,
        text: capacity?.gspGroup
    },
    {
        name: "Supplier Name",
        icon: faFileContract,
        text: capacity?.supplierName
    },
    {
        name: "Serial Number",
        icon: faBarcode,
        text: capacity?.serialNumber
    },
    {
        name: "Distributor Name",
        icon: faShuttleVan,
        text: capacity?.distributorName
    },
    {
        name: "Measurement Class",
        icon: faRulerVertical,
        text: capacity?.measurementClass
    }
]

export const chart = (capacity: CapacityModel, plotLinesX, plotLinesY, usagePlotlines, usagePlotbands) => {
    return {
        series: {
            "capacity": [
                {
                    name: "Active Energy (kWh)",
                    type: "column",
                    data: capacity?.usageChart.series.activeEnergy,
                    color: "#6c5ce7",
                    visible: false
                },
                {
                    name: "Apparent Power (kVA)",
                    type: "spline",
                    lineWidth: 4,
                    yAxis: 1,
                    data: capacity?.usageChart.series.apparentPower,
                    color: "#38ada9"
                }
            ]
        },
    
        xAxis: {
            "capacity": {
                lineColor: "#ced6e0",
                gridLineDashStyle: 'shortdash',
                labels: {
                    style: {
                        fontWeight: 'bold',
                        fontSize: '10px'
                    }
                },
                categories: capacity?.usageChart.labels.categories
            }
        },
    
        yAxis: {
            "capacity" : [
                {
                    showEmpty: false,
                    title: {
                        text: `<div style="text-transform: uppercase">${capacity?.usageChart.labels.activeEnergy.yAxisTitle}</div>`,
                        margin: 20,
                        style: {
                            color: "#6c5ce7",
                            fontWeight: 'bold',
                            fontSize: '13px'
                        }
                    },
                    labels: {
                        style: {
                            color: "#6c5ce7",
                            fontWeight: 'bold',
                            fontSize: '13px'
                        }
                    },
                    gridLineColor: "#dfe4ea",
                    gridLineDashStyle: 'shortdash'
                },
                {
                    showEmpty: false,
                    title: {
                        text: `<div style="text-transform: uppercase">${capacity?.usageChart.labels.apparentPower.yAxisTitle}</div>`,
                        margin: 20,
                        style: {
                            color: "#38ada9",
                            fontWeight: 'bold',
                            fontSize: '13px'
                        }
                    },
                    labels: {
                        style: {
                            color: "#38ada9",
                            fontWeight: 'bold',
                            fontSize: '13px'
                        }
                    },
                    min: 0,
                    max: capacity ? Math.max(...capacity?.usageChart.annotations.map(lab => lab.value), capacity?.consumptionStats.maxDemand, capacity.importCapacity) * 1.04 : null,
                    opposite: true,
                    gridLineColor: "#dfe4ea",
                    gridLineDashStyle: 'shortdash',
                    plotLines: usagePlotlines,
                    plotBands: usagePlotbands
                }
            ]
        }
    }
}
